import React, { useState } from "react";
import { useI18n } from "../i18n/context";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { BOOKING_FORM } from "../constants/testIds";
import { VEHICLES } from "../data/vehicles";
import { SERVICES } from "../data/services";
import { api } from "../lib/api";
import { trackConversion, toPlatformWhatsAppUrl } from "../lib/whatsapp";

import BookingPolicyNotice from "./BookingPolicyNotice";
import { getRouteAvailability } from "../lib/destinationBookingGate";
const initialState = {
    name: "",
    mobile: "",
    pickup: "",
    drop: "",
    trip_date: "",
    vehicle: "",
    service_type: "",
    message: "",
};

export default function BookingForm({
    variant = "light",
    sourcePage = "homepage",
    defaultVehicle,
    pickupPlaceholder = "Enter pickup city or location",
    heading,
    subheading,
    dense = false,
    routeConstraint,
}) {
    const { t } = useI18n();
    const [form, setForm] = useState({
        ...initialState,
        vehicle: defaultVehicle || "",
        pickup: routeConstraint?.pickup || "",
        drop: routeConstraint?.drop || "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [policyAccepted, setPolicyAccepted] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!policyAccepted) { toast.error(t("Please read and accept the booking, cancellation and refund policies.")); return; }
        if (!form.name || !form.mobile || !form.pickup) {
            toast.error(t("Please fill Name, Mobile & Pickup location."));
            return;
        }
        if (!/^\+?[0-9\s-]{10,15}$/.test(form.mobile)) {
            toast.error(t("Please enter a valid mobile number."));
            return;
        }
        if (routeConstraint) {
            const selected = VEHICLES.find(v => v.name === form.vehicle);
            const allowed = routeConstraint.vehicleSlugs.includes(selected?.slug) && getRouteAvailability(routeConstraint.policy, form.pickup, form.drop, selected?.slug, routeConstraint.destinationPath).bookable;
            if (!allowed) { toast.error(t("This route and vehicle are not confirmed. Please select a permitted option.")); return; }
        }
        setSubmitting(true);
        try {
            const res = await api.createBooking({
                ...form,
                source_page: sourcePage,
            });
            trackConversion("booking_form_submit", { source: sourcePage });
            toast.success(t(res.message || "Enquiry received! Continuing on WhatsApp…"));
            if (res.whatsapp_url) {
                setTimeout(() => {
                    window.open(toPlatformWhatsAppUrl(res.whatsapp_url), "_blank", "noopener,noreferrer");
                }, 400);
            }
            setForm({ ...initialState, vehicle: defaultVehicle || "", pickup: routeConstraint?.pickup || "", drop: routeConstraint?.drop || "" });
            setPolicyAccepted(false);
        } catch (err) {
            toast.error(t("Could not send your enquiry. Please try again or WhatsApp us directly."));
        } finally {
            setSubmitting(false);
        }
    };

    const isDark = variant === "dark";
    const labelCls = isDark ? "text-white/85" : "text-brand-navy";
    const inputCls = isDark
        ? "bg-white/10 border border-white/15 text-white placeholder:text-white/40 focus:border-brand-gold focus:ring-brand-gold/40"
        : "bg-white border border-brand-navy/10 text-brand-navy placeholder:text-brand-slate/60 focus:border-brand-gold focus:ring-brand-gold/40";

    return (
        <form
            data-testid={BOOKING_FORM.root}
            onSubmit={onSubmit}
            className={`w-full rounded-2xl ${
                isDark
                    ? "glass-dark p-5 md:p-6"
                    : "bg-white border border-brand-navy/10 p-6 md:p-8 shadow-soft"
            }`}
        >
            {heading && (
                <div className="mb-5">
                    <div
                        className={`label-eyebrow ${
                            isDark ? "text-brand-gold" : "text-brand-gold"
                        }`}
                    >
                        Booking Enquiry
                    </div>
                    <h3
                        className={`mt-2 font-display text-2xl font-extrabold ${
                            isDark ? "text-white" : "text-brand-navy"
                        }`}
                    >
                        {heading}
                    </h3>
                    {subheading && (
                        <p
                            className={`mt-1 text-sm ${
                                isDark ? "text-white/70" : "text-brand-slate"
                            }`}
                        >
                            {subheading}
                        </p>
                    )}
                </div>
            )}

            <div className={`grid ${dense ? "gap-3 sm:grid-cols-2" : "gap-4 md:grid-cols-2"}`}>
                <Field label="Full Name" required labelCls={labelCls}>
                    <input
                        data-testid={BOOKING_FORM.name}
                        type="text"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="e.g. Ravi Sharma"
                        className={`w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 ${inputCls}`}
                    />
                </Field>
                <Field label="Mobile Number" required labelCls={labelCls}>
                    <input
                        data-testid={BOOKING_FORM.mobile}
                        type="tel"
                        required
                        value={form.mobile}
                        onChange={set("mobile")}
                        placeholder="+91 98765 43210"
                        className={`w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 ${inputCls}`}
                    />
                </Field>
                <Field label="Pickup Location" required labelCls={labelCls}>
                    <input
                        data-testid={BOOKING_FORM.pickup}
                        type="text"
                        required
                        readOnly={Boolean(routeConstraint)}
                        value={form.pickup}
                        onChange={set("pickup")}
                        placeholder={pickupPlaceholder}
                        className={`w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 ${inputCls}`}
                    />
                </Field>
                <Field label="Drop Location" labelCls={labelCls}>
                    <input
                        data-testid={BOOKING_FORM.drop}
                        type="text"
                        readOnly={Boolean(routeConstraint)}
                        value={form.drop}
                        onChange={set("drop")}
                        placeholder="Enter destination city or location"
                        className={`w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 ${inputCls}`}
                    />
                </Field>
                <Field label="Travel Date" labelCls={labelCls}>
                    <input
                        data-testid={BOOKING_FORM.date}
                        type="date"
                        value={form.trip_date}
                        onChange={set("trip_date")}
                        className={`w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 ${inputCls}`}
                    />
                </Field>
                <Field label="Preferred Vehicle" labelCls={labelCls}>
                    <select
                        data-testid={BOOKING_FORM.vehicle}
                        value={form.vehicle}
                        onChange={set("vehicle")}
                        className={`w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 ${inputCls}`}
                    >
                        <option value="">Select vehicle</option>
                        {VEHICLES.filter(v => !routeConstraint || routeConstraint.vehicleSlugs.includes(v.slug)).map((v) => (
                            <option key={v.slug} value={v.name}>
                                {v.name}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field label="Service Type" labelCls={labelCls} className={dense ? "sm:col-span-2" : "md:col-span-2"}>
                    <select
                        data-testid={BOOKING_FORM.service}
                        value={form.service_type}
                        onChange={set("service_type")}
                        className={`w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 ${inputCls}`}
                    >
                        <option value="">Select service type</option>
                        {SERVICES.map((s) => (
                            <option key={s.slug} value={s.title}>
                                {s.title}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field
                    label="Message (optional)"
                    labelCls={labelCls}
                    className={dense ? "sm:col-span-2" : "md:col-span-2"}
                >
                    <textarea
                        data-testid={BOOKING_FORM.message}
                        rows={3}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Any special requirements? Number of passengers, luggage, timings…"
                        className={`w-full resize-none rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2 ${inputCls}`}
                    />
                </Field>
            </div>

            <BookingPolicyNotice className="mt-5" dark={isDark} accepted={policyAccepted} onChange={setPolicyAccepted} />
            <div className="mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                <button
                    type="submit"
                    data-testid={BOOKING_FORM.submit}
                    disabled={submitting}
                    className="btn-gold flex-1 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                        </>
                    ) : (
                        <>
                            <Send className="h-4 w-4" /> Send Enquiry & Continue on WhatsApp
                        </>
                    )}
                </button>
            </div>
            <p
                className={`mt-3 text-[11px] ${
                    isDark ? "text-white/60" : "text-brand-slate"
                }`}
            >
                We use your details to respond to this enquiry and coordinate your journey.
            </p>
        </form>
    );
}

function Field({ label, required, children, labelCls, className = "" }) {
    return (
        <label className={`flex flex-col gap-1.5 ${className}`}>
            <span className={`text-xs font-semibold ${labelCls}`}>
                {label}
                {required && <span className="text-brand-gold"> *</span>}
            </span>
            {children}
        </label>
    );
}
